/**
 * API Configuration Constants
 * Centralized configuration for all API operations
 */

// ============================================================================
// API BASE PATH
// ============================================================================

export const API_BASE_PATH = "/unifiedtime";

// ============================================================================
// QUERY CONFIGURATION
// ============================================================================

/**
 * Cache and stale time configurations for different query types
 * staleTime: How long data is considered fresh before refetching
 * gcTime: How long unused data is kept in cache before garbage collection
 */
export const QUERY_CONFIG = {
  // Calendar — longer stale reduces repeat work when switching tabs/months; data refetches on range change.
  calendar: {
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  },
  // Account status - 30 seconds stale, 5 minutes cache
  accountStatus: {
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  },
  // Booking slots - 2 minutes stale, default cache
  booking: {
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  // External events - 5 minutes stale, 10 minutes cache
  externalEvents: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
};

// ============================================================================
// RETRY CONFIGURATION
// ============================================================================

export const RETRY_CONFIG = {
  // Standard retry count for most operations
  standard: 3,
  // No retry for certain operations
  none: 0,
};

// ============================================================================
// OAUTH CONFIGURATION
// ============================================================================

export const OAUTH_CONFIG = {
  // Maximum attempts to get auth token after OAuth redirect
  maxAttempts: 30,
  // Delay between retry attempts (in milliseconds)
  retryDelay: 100,
  // Total timeout: 30 * 100ms = 3 seconds
  totalTimeout: 3000,
};

// ============================================================================
// ERROR HANDLING
// ============================================================================

export const ERROR_CODES = {
  // Calendar not connected for this email
  CALENDAR_NOT_CONNECTED: 422,
  // Unauthorized
  UNAUTHORIZED: 401,
  // Forbidden
  FORBIDDEN: 403,
  // Not found
  NOT_FOUND: 404,
  // Server error
  SERVER_ERROR: 500,
};

// ============================================================================
// ENDPOINTS
// ============================================================================

export const ENDPOINTS = {
  calendar: {
    google: `${API_BASE_PATH}/getCalendar/google`,
    googleExternal: `${API_BASE_PATH}/getCalendar/google/external`,
    microsoft: `${API_BASE_PATH}/getCalendar/msft`,
    microsoftExternal: `${API_BASE_PATH}/getCalendar/msft/external`,
    professionalEmail: `${API_BASE_PATH}/getCalendar/professional-email`,
  },
  booking: {
    openSlots: `${API_BASE_PATH}/openslots`,
    externalEventBooking: `${API_BASE_PATH}/external-event-booking`,
  },
  account: {
    status: `${API_BASE_PATH}/status`,
    clear: `${API_BASE_PATH}/clear`,
    removeConnection: `${API_BASE_PATH}/remove-connection`,
  },
  crm: {
    search: `${API_BASE_PATH}/../monday/search`,
  },
  events: {
    createGoogle: `${API_BASE_PATH}/../calendar/google/create`,
    createMicrosoft: `${API_BASE_PATH}/../calendar/microsoft/create`,
    createProfessionalEmail: `${API_BASE_PATH}/professional-email/create`,
  },
};

// ============================================================================
// TOAST MESSAGES
// ============================================================================

export const TOAST_MESSAGES = {
  booking: {
    success: {
      title: "Success",
      description: "Your meeting has been scheduled successfully!",
    },
    error: {
      title: "Error",
      description: "Booking failed. Please try again.",
    },
  },
  account: {
    resetSuccess: {
      title: "Account Reset",
      description: "Your account has been reset successfully.",
    },
    resetError: {
      title: "Error",
      description: "Failed to reset account. Please try again.",
    },
    connectionRemoved: {
      title: "Connection Removed",
      description: "Connection removed successfully.",
    },
    connectionError: {
      title: "Error",
      description: "Failed to remove connection. Please try again.",
    },
  },
  calendar: {
    createSuccess: {
      title: "Success",
      description: "Calendar event created successfully!",
    },
    createError: {
      title: "Error",
      description: "Failed to create calendar event.",
    },
    updateSuccess: {
      title: "Success",
      description: "Calendar event updated successfully!",
    },
    updateError: {
      title: "Error",
      description: "Failed to update calendar event.",
    },
    deleteSuccess: {
      title: "Success",
      description: "Calendar event deleted successfully!",
    },
    deleteError: {
      title: "Error",
      description: "Failed to delete calendar event.",
    },
  },
};
